// frontend/src/pages/MarketsOverview.jsx
import React from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";

export default function MarketsOverview() {
  const navigate = useNavigate();

  // --------- sample data (replace with live later) ----------
  const kpis = [
    { label: "Total Market Cap", value: "₹192.4T", delta: "+1.2%" },
    { label: "24h Volume", value: "₹3.18T", delta: "-0.8%" },
    { label: "BTC Dominance", value: "52.7%", delta: "+0.2%" },
    { label: "Gainers / Losers", value: "312 / 198", delta: "" },
  ];

  const movers = [
    { sym: "BTC", name: "Bitcoin", price: "₹2,564,000", change: "+2.1%" },
    { sym: "ETH", name: "Ethereum", price: "₹156,800", change: "+1.4%" },
    { sym: "SOL", name: "Solana", price: "₹12,450", change: "-0.9%" },
    { sym: "XRP", name: "XRP", price: "₹55.80", change: "+3.6%" },
    { sym: "DOGE", name: "Dogecoin", price: "₹12.40", change: "-1.1%" },
  ];

  const pairs = [
    { pair: "BTC/INR", price: "₹2,564,000", change: "+1.5%", volume: "₹1.2B" },
    { pair: "ETH/INR", price: "₹156,800", change: "-0.7%", volume: "₹840M" },
    { pair: "SOL/INR", price: "₹12,450", change: "+3.2%", volume: "₹210M" },
    { pair: "MATIC/INR", price: "₹58.70", change: "-0.4%", volume: "₹95M" },
    { pair: "USDT/INR", price: "₹83.10", change: "+0.1%", volume: "₹2.3B" },
  ];

  const isUp = (s) => String(s).trim().startsWith("+");

  return (
    <motion.div
      className="min-h-screen bg-blue-50 dark:bg-slate-900 pb-16"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.35 }}
    >
      {/* Header */}
      <header className="bg-white dark:bg-slate-800 shadow-md border-b-4 border-blue-600">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10 md:py-14 text-center">
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-blue-800 dark:text-slate-100 mb-2 md:mb-3">
            Markets Overview
          </h1>
          <p className="text-sm sm:text-base md:text-lg text-blue-600 dark:text-slate-300 max-w-2xl mx-auto">
            Track live prices, 24h changes and volumes for top crypto assets on Coinzetto.
          </p>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-3 sm:px-4 md:px-6 mt-6 md:mt-10 space-y-8 md:space-y-12">
        {/* KPIs */}
        <section>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-5">
            {kpis.map((k) => (
              <div
                key={k.label}
                className="bg-white dark:bg-slate-900 rounded-xl border border-blue-100 dark:border-slate-700 shadow-sm px-3 py-3 md:px-4 md:py-4"
              >
                <p className="text-[11px] sm:text-xs text-gray-500 dark:text-slate-400">
                  {k.label}
                </p>
                <div className="mt-1 flex items-end justify-between">
                  <p className="text-base sm:text-lg md:text-xl font-semibold text-gray-900 dark:text-slate-100">
                    {k.value}
                  </p>
                  {!!k.delta && (
                    <span
                      className={`text-[11px] sm:text-xs px-2 py-0.5 rounded-full ${
                        isUp(k.delta)
                          ? "bg-green-50 text-green-700 dark:bg-emerald-900/30 dark:text-emerald-300"
                          : "bg-red-50 text-red-700 dark:bg-rose-900/30 dark:text-rose-300"
                      }`}
                    >
                      {k.delta}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Filters / Search (placeholder) */}
        <section className="bg-white dark:bg-slate-800 rounded-2xl border border-blue-100 dark:border-slate-700 shadow-sm p-3 sm:p-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div className="flex items-center gap-2">
              {["All", "BTC", "ETH", "Stablecoins"].map((f, i) => (
                <button
                  key={f}
                  type="button"
                  className={`text-xs sm:text-sm px-3 py-1.5 rounded-full border transition ${
                    i === 0
                      ? "bg-blue-50 text-blue-700 border-blue-200 dark:bg-slate-900 dark:text-slate-100 dark:border-slate-700"
                      : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50 dark:bg-slate-900 dark:text-slate-100 dark:border-slate-700 dark:hover:bg-slate-900/70"
                  }`}
                >
                  {f}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search pair (e.g. BTC/INR)"
                  className="text-sm w-48 sm:w-64 md:w-72 rounded-lg px-3 py-2 border border-blue-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-blue-800 dark:text-slate-100 placeholder:text-blue-400 dark:placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-200 dark:focus:ring-blue-900"
                />
              </div>
              <select className="text-sm rounded-lg px-2.5 py-2 border border-blue-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-blue-800 dark:text-slate-100 focus:outline-none">
                <option>INR</option>
                <option>USD</option>
              </select>
            </div>
          </div>
        </section>

        {/* Top Movers */}
        <section>
          <h2 className="text-lg sm:text-xl md:text-2xl font-semibold text-blue-800 dark:text-slate-100 mb-3 md:mb-4">
            Top Movers (24h)
          </h2>
          <div className="overflow-x-auto">
            <div className="flex gap-3">
              {movers.map((m) => (
                <div
                  key={m.sym}
                  className="min-w-[180px] sm:min-w-[200px] bg-white dark:bg-slate-800 rounded-xl border border-blue-100 dark:border-slate-700 shadow-sm p-3"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-semibold text-gray-900 dark:text-slate-100">
                        {m.sym}
                      </p>
                      <p className="text-[11px] text-gray-500 dark:text-slate-400">
                        {m.name}
                      </p>
                    </div>
                    <span
                      className={`text-xs px-2 py-0.5 rounded-full ${
                        isUp(m.change)
                          ? "bg-green-50 text-green-700 dark:bg-emerald-900/30 dark:text-emerald-300"
                          : "bg-red-50 text-red-700 dark:bg-rose-900/30 dark:text-rose-300"
                      }`}
                    >
                      {m.change}
                    </span>
                  </div>
                  <p className="mt-2 text-sm text-gray-700 dark:text-slate-200">
                    {m.price}
                  </p>
                  {/* Mini sparkline placeholder */}
                  <div className="mt-2 h-8 w-full rounded bg-gradient-to-r from-blue-50 to-blue-100 dark:from-slate-800 dark:to-slate-700" />
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Markets List */}
        <section>
          <h2 className="text-lg sm:text-xl md:text-2xl font-semibold text-blue-800 dark:text-slate-100 mb-3 md:mb-4">
            Market Pairs
          </h2>

          {/* Mobile cards */}
          <div className="md:hidden space-y-2">
            {pairs.map((m) => (
              <div
                key={m.pair}
                className="bg-white dark:bg-slate-800 rounded-xl border border-blue-100 dark:border-slate-700 shadow-sm p-3 flex items-center justify-between"
              >
                <div>
                  <p className="text-sm font-semibold text-gray-900 dark:text-slate-100">
                    {m.pair}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-slate-400">
                    Vol: {m.volume}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-800 dark:text-slate-200">
                    {m.price}
                  </p>
                  <p
                    className={`text-xs font-medium ${
                      isUp(m.change)
                        ? "text-green-600 dark:text-emerald-300"
                        : "text-red-600 dark:text-rose-300"
                    }`}
                  >
                    {m.change}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Desktop table */}
          <div className="hidden md:block overflow-hidden bg-white dark:bg-slate-800 rounded-xl border border-blue-100 dark:border-slate-700 shadow-sm">
            <div className="overflow-x-auto">
              <table className="min-w-full text-left text-gray-700 dark:text-slate-200">
                <thead className="bg-blue-50 text-blue-800 dark:bg-slate-900/60 dark:text-slate-100 text-sm uppercase sticky top-0">
                  <tr>
                    <th className="px-5 py-3">Pair</th>
                    <th className="px-5 py-3">Price</th>
                    <th className="px-5 py-3">24h Change</th>
                    <th className="px-5 py-3">Volume</th>
                  </tr>
                </thead>
                <tbody>
                  {pairs.map((m) => (
                    <tr
                      key={m.pair}
                      className="border-t border-blue-100 dark:border-slate-700 hover:bg-gray-50 dark:hover:bg-slate-900/60"
                    >
                      <td className="px-5 py-3 font-medium">{m.pair}</td>
                      <td className="px-5 py-3">{m.price}</td>
                      <td
                        className={`px-5 py-3 ${
                          isUp(m.change)
                            ? "text-green-600 dark:text-emerald-300"
                            : "text-red-600 dark:text-rose-300"
                        }`}
                      >
                        {m.change}
                      </td>
                      <td className="px-5 py-3">{m.volume}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="px-5 py-3 text-xs text-gray-500 dark:text-slate-400 border-t border-blue-100 dark:border-slate-700 bg-white dark:bg-slate-800">
              * Sample data shown. Live order books & charts coming soon.
            </div>
          </div>
        </section>

        {/* CTA / Info */}
        <section className="bg-white dark:bg-slate-800 rounded-2xl border border-blue-100 dark:border-slate-700 shadow-sm p-4 sm:p-6 text-center">
          <h3 className="text-lg sm:text-xl md:text-2xl font-semibold text-blue-800 dark:text-slate-100">
            Full Exchange Experience — Coming Soon
          </h3>
          <p className="text-xs sm:text-sm text-gray-600 dark:text-slate-300 mt-1 sm:mt-2 max-w-2xl mx-auto">
            Our production interface will include live charts, depth, advanced order types
            and real-time liquidity. Early access users get priority for trading contests.
          </p>
          <div className="mt-3 sm:mt-4 flex items-center justify-center gap-2">
            <button
              type="button"
              onClick={() => navigate("/quick-trade")}
              className="px-4 py-2 sm:px-5 sm:py-2.5 rounded-lg text-sm sm:text-base font-semibold bg-blue-600 text-white hover:bg-blue-700"
            >
              Go to Quick Trade
            </button>
            <button
              type="button"
              onClick={() => navigate("/market")}
              className="px-4 py-2 sm:px-5 sm:py-2.5 rounded-lg text-sm sm:text-base font-semibold border border-blue-200 dark:border-slate-700 text-blue-800 dark:text-slate-100 bg-white dark:bg-slate-900 hover:bg-gray-50 dark:hover:bg-slate-900/70"
            >
              View Markets
            </button>
          </div>
        </section>

        {/* Disclaimer */}
        <section className="text-center">
          <p className="text-[11px] sm:text-xs text-gray-500 dark:text-slate-400">
            Prices are for illustration. Not investment advice. Crypto assets are volatile —
            do your own research.
          </p>
        </section>
      </main>
    </motion.div>
  );
}
